import { defineMessages } from 'react-intl';
import { MASTER_COLOR_LIST, getColorChoices, getButtonChoices, getDefaultButton } from '../../../config/colors';

const messages = defineMessages({
  defaultTitle: {
    id: 'Embla Carousel',
    defaultMessage: 'Embla Carousel',
  },
});

const isNotMarquee = (formData) => formData?.displayMode !== 'logo-marquee';
const isContentMode = (formData) =>
  !formData?.displayMode ||
  formData?.displayMode === 'full' ||
  formData?.displayMode === 'image-top' ||
  formData?.displayMode === 'image-top-panel';
const isArrowTop = (formData) => formData?.arrowPosition === 'top';
const isAutoplay = (formData) => !!formData?.autoplay;

const emblaCarouselSchema = ({ formData } = {}) => {
  const backgroundColor = formData?.backgroundColor;
  return ({
  title: messages.defaultTitle,
  fieldsets: [
    {
      id: 'default',
      title: 'Content',
      fields: [
        'title',
        'description',
        'useListing',
        'filterTags',
      ],
    },
    {
      id: 'card',
      title: 'Card layout',
      fields: [
        'displayMode',
        'slideButtonStyle',
        'dateDisplay',
        'hideDescription',
        'hideButtons',
        'clickableSlides',
        'equalHeight',
      ],
    },
    {
      id: 'behaviour',
      title: 'Scrolling & behaviour',
      fields: [
        'slidesToShow',
        'minSlidesOnMobile',
        'centeredPeek',
        'loop',
        'autoplay',
        'autoplayDelay',
        'autoHeight',
      ],
    },
    {
      id: 'navigation',
      title: 'Navigation',
      fields: [
        'arrowPosition',
        'headerLinkText',
        'headerLinkUrl',
        'headerLinkStyle',
        'arrowStyle',
        'hideArrows',
        'hideDots',
      ],
    },
    {
      id: 'appearance',
      title: 'Appearance',
      fields: [
        'alignment',
        'isFullWidth',
        'backgroundColor',
        'textTone',
        'outerClassName',
      ],
    },
    {
      id: 'marquee',
      title: 'Logo marquee options',
      fields: [
        'marqueeHelp',
        'marqueeSpeed',
        'logoHeight',
        'logoGap',
        'logoPadding',
        'pauseOnHover',
      ],
    },
  ],

  properties: {
    // Content
    title: {
      title: 'Heading',
      description: 'Optional heading shown above the carousel (h2)',
      type: 'string',
    },
    description: {
      title: 'Intro text',
      description: 'Optional text shown below the heading, above the carousel',
      type: 'string',
    },
    filterTags: {
      title: 'Filter tabs',
      description:
        'Comma-separated list of tags to show as filter buttons (e.g. "News, Events, Resources"). An "All" tab is added automatically. Tabs with no matching results are hidden.',
      type: 'string',
    },
    slides: {
      title: 'Slides',
      widget: 'object_list',
      schema: {
        title: 'Slide',
        titleField: 'heading',
        fieldsets: [
          {
            id: 'default',
            title: 'Default',
            fields: ['preheader', 'heading', 'content', 'richText', 'image', 'link', 'buttonText'],
          },
        ],
        properties: {
          preheader: { title: 'Preheader', description: 'Small label shown above the heading', type: 'string' },
          heading: { title: 'Heading', type: 'string' },
          content: { title: 'Summary', description: 'Plain text summary (used as fallback if no rich text)', type: 'text' },
          richText: { title: 'Rich text content', widget: 'richtext' },
          image: {
            title: 'Image',
            widget: 'object_browser',
            mode: 'image',
            allowExternals: false,
          },
          link: {
            title: 'Link',
            widget: 'object_browser',
            mode: 'link',
            allowExternals: true,
            multi: false,
            default: null,
          },
          buttonText: { title: 'Button text', type: 'string' },
        },
        required: [],
      },
    },

    // Card layout
    displayMode: {
      title: 'Card style',
      type: 'string',
      choices: [
        ['full', 'Image with text overlay'],
        ['image-only', 'Image only'],
        ['image-top', 'Image above content'],
        ['image-top-panel', 'Image above shared content panel'],
        ['logo-marquee', 'Scrolling logo strip'],
      ],
      default: 'full',
    },
    dateDisplay: {
      title: 'Show date',
      type: 'string',
      choices: [
        ['none', "Don't show a date"],
        ['effective', 'Publication date'],
        ['start', 'Event start date (falls back to publication date)'],
      ],
      default: 'none',
    },
    hideDescription: {
      title: 'Hide description',
      type: 'boolean',
      default: false,
    },
    hideButtons: {
      title: 'Hide "Read more" buttons',
      type: 'boolean',
      default: false,
    },
    clickableSlides: {
      title: 'Make entire card clickable',
      description: 'Clicking anywhere on the card follows the slide link',
      type: 'boolean',
      default: false,
    },
    equalHeight: {
      title: 'Equal height cards',
      description: 'Stretches all cards to match the tallest one in the row',
      type: 'boolean',
      default: false,
    },
    slideButtonStyle: {
      title: 'Button style',
      widget: 'select',
      choices: getButtonChoices(MASTER_COLOR_LIST),
      default: getDefaultButton(backgroundColor, MASTER_COLOR_LIST),
    },

    // Scrolling & behaviour
    slidesToShow: {
      title: 'Cards visible at once',
      description: 'On desktop screens',
      type: 'number',
      default: 1,
    },
    minSlidesOnMobile: {
      title: 'Cards visible on mobile',
      description: 'On screens 768px wide and below',
      type: 'number',
      default: 1,
    },
    centeredPeek: {
      title: 'Centre active card',
      description:
        'The active card centres itself on screen; neighbouring cards peek in from both sides',
      type: 'boolean',
      default: false,
    },
    loop: {
      title: 'Loop continuously',
      type: 'boolean',
      default: true,
    },
    autoplay: {
      title: 'Autoplay',
      type: 'boolean',
    },
    autoplayDelay: {
      title: 'Time between slides (ms)',
      description: '8000 = 8 seconds',
      type: 'number',
      default: 8000,
    },
    autoHeight: {
      title: 'Auto height',
      description: 'Resize the carousel to match the height of the active slide',
      type: 'boolean',
      default: false,
    },

    // Navigation
    arrowPosition: {
      title: 'Arrow position',
      type: 'string',
      choices: [
        ['bottom', 'Below the carousel'],
        ['top', 'Top right, beside the heading'],
      ],
      default: 'bottom',
    },
    headerLinkText: {
      title: 'Button label',
      description: 'Optional button shown between the arrows when arrows are at the top',
      type: 'string',
    },
    headerLinkUrl: {
      title: 'Button link',
      widget: 'object_browser',
      mode: 'link',
      allowExternals: true,
      multi: false,
      default: null,
    },
    headerLinkStyle: {
      title: 'Button style',
      widget: 'select',
      choices: getButtonChoices(MASTER_COLOR_LIST),
      default: getDefaultButton(backgroundColor, MASTER_COLOR_LIST),
    },
    arrowStyle: {
      title: 'Arrow colour',
      widget: 'select',
      choices: getButtonChoices(MASTER_COLOR_LIST),
      default: getDefaultButton(backgroundColor, MASTER_COLOR_LIST),
    },
    listingButtonStyle: {
      title: 'Button style',
      description: 'Style for the \'Read more\' button on each result',
      widget: 'select',
      choices: getButtonChoices(MASTER_COLOR_LIST),
      default: getDefaultButton(backgroundColor, MASTER_COLOR_LIST),
    },
    hideArrows: {
      title: 'Hide arrows',
      type: 'boolean',
    },
    hideDots: {
      title: 'Hide page dots',
      type: 'boolean',
    },

    // Appearance
    alignment: {
      title: 'Text alignment',
      widget: 'align',
      actions: ['left', 'center', 'right'],
      default: 'left',
    },
    isFullWidth: {
      title: 'Full width',
      type: 'boolean',
      default: false,
    },
    backgroundColor: {
      title: 'Background colour',
      widget: 'select',
      choices: [
        ['transparent', 'None'],
        ...getColorChoices(MASTER_COLOR_LIST),
      ],
      default: 'transparent',
    },
    textTone: {
      title: 'Heading & dots colour',
      widget: 'select',
      choices: [
        ['light', 'Light (for dark backgrounds)'],
        ['dark', 'Dark (for light backgrounds)'],
      ],
      default: 'dark',
    },
    outerClassName: {
      title: 'Custom CSS class',
      description: 'Extra class added to the outer wrapper (for theme overrides)',
      type: 'string',
    },

    // Logo marquee
    marqueeHelp: {
      title: 'How to use',
      description:
        'Add slides with images (your logos). Only the image is used — text is ignored. Add a link to a slide to make that logo clickable.',
      type: 'string',
    },
    marqueeSpeed: {
      title: 'Scroll speed (seconds)',
      description: 'Time to complete one full loop',
      type: 'number',
      default: 20,
    },
    logoHeight: {
      title: 'Logo height (px)',
      type: 'number',
      default: 48,
    },
    logoGap: {
      title: 'Gap between logos (px)',
      type: 'number',
      default: 32,
    },
    logoPadding: {
      title: 'Logo padding (px)',
      type: 'number',
      default: 8,
    },
    pauseOnHover: {
      title: 'Pause when hovered',
      type: 'boolean',
      default: true,
    },
  },

  required: [],

  // Fieldset-level visibility
  fieldset_condition: {
    card: isNotMarquee,
    behaviour: isNotMarquee,
    navigation: isNotMarquee,
    marquee: (formData) => formData?.displayMode === 'logo-marquee',
  },

  // Field-level conditional visibility
  field_condition: {
    filterTags: (formData) => !!formData?.useListing,
    dateDisplay: isContentMode,
    hideDescription: isContentMode,
    hideButtons: isContentMode,
    clickableSlides: isNotMarquee,
    equalHeight: isNotMarquee,
    autoplayDelay: isAutoplay,
    headerLinkText: isArrowTop,
    headerLinkUrl: isArrowTop,
    headerLinkStyle: isArrowTop,
    arrowStyle: isArrowTop,
    listingButtonStyle: (formData) => !!formData?.useListing,
  },
});
};

export default emblaCarouselSchema;
